import React, { useMemo, useState, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import useOperationalStore, {
  PIPELINE,
  computeSlaRisk,
  fmtDur,
  getStageForStatus,
  getStageAge,
  groupJobsByPipelineStage,
} from '../../platform/store/useOperationalStore';
import * as mutations from '../../graphql/mutations';
import { AlertTriangle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const client = generateClient();

const RISK_COLOR = {
  CRITICAL: '#dc2626',
  HIGH:     '#ea580c',
  MEDIUM:   '#ca8a04',
  LOW:      '#2563eb',
};

function MobileTruckCard({ truck, onLongPress, isBeingMoved, moveMode }) {
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  const stage = getStageForStatus(truck.status);
  const risk = computeSlaRisk(truck) || 'LOW';
  const age = getStageAge(truck);
  const accent = RISK_COLOR[risk] || RISK_COLOR.LOW;
  const techs = truck.techNumbers?.filter(Boolean) || [];

  const longPressTimer = useRef(null);
  const didLongPress = useRef(false);
  const touchMoved = useRef(false);

  function handleTouchStart() {
    didLongPress.current = false;
    touchMoved.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      if (navigator.vibrate) navigator.vibrate(60);
      onLongPress(truck);
    }, 500);
  }

  function handleTouchMove() {
    touchMoved.current = true;
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function handleTouchEnd() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function handleClick() {
    if (didLongPress.current) return;
    if (moveMode) return;
    setSelectedTruckId(truck.id);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        width: '100%',
        textAlign: 'left',
        background: isBeingMoved ? '#fef3c7' : '#fff',
        border: isBeingMoved ? `2px solid ${accent}` : '1px solid #e2e8f0',
        borderLeft: `5px solid ${accent}`,
        borderRadius: 8,
        padding: 12,
        boxShadow: isBeingMoved
          ? `0 6px 20px rgba(0,0,0,0.2)`
          : '0 1px 4px rgba(15,23,42,0.08)',
        transform: isBeingMoved ? 'scale(1.02)' : 'scale(1)',
        transition: 'transform 0.15s, box-shadow 0.15s',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        opacity: moveMode && !isBeingMoved ? 0.5 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>
              {truck.repairOrder}
            </span>
            {truck.isHardDown && <AlertTriangle size={15} color="#dc2626" />}
            {isBeingMoved && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#92400e',
                background: '#fef3c7', borderRadius: 4, padding: '2px 6px',
                border: '1px solid #f59e0b',
              }}>
                MOVING
              </span>
            )}
          </div>
          <div style={{ marginTop: 4, fontSize: 14, fontWeight: 800, color: '#334155' }}>
            {truck.companyName || 'No company'}
          </div>
          <div style={{ marginTop: 2, fontSize: 12, color: '#64748b' }}>
            {truck.truckNumber || 'No unit'}{techs.length ? ` · Tech ${techs.join(', ')}` : ''}
          </div>
        </div>
        <span style={{
          flexShrink: 0, borderRadius: 999,
          background: '#eff6ff', color: '#2563eb',
          padding: '4px 8px', fontSize: 11, fontWeight: 900,
        }}>
          {stage?.label || truck.status}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b' }}>
          <Clock size={13} />
          {fmtDur(age.stageMs)} in stage
        </span>
        {risk !== 'LOW' && (
          <span style={{ fontSize: 11, fontWeight: 900, color: accent }}>{risk}</span>
        )}
        {truck.warranty && (
          <span style={{ fontSize: 11, fontWeight: 800, color: '#92400e' }}>WARRANTY</span>
        )}
      </div>
    </button>
  );
}

export default function MobileKanbanBoard() {
  const trucks      = useOperationalStore(s => s.trucks);
  const fetchAll    = useOperationalStore(s => s.fetchAll);
  const loading     = useOperationalStore(s => s.loading);
  const error       = useOperationalStore(s => s.error);

  const [stageIdx, setStageIdx]           = useState(0);
  const [movingTruckId, setMovingTruckId] = useState(null);
  const [saving, setSaving]               = useState(false);

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const stageGroups = useMemo(
    () => groupJobsByPipelineStage(trucks).map(({ stage, jobs }) => ({ stage, trucks: jobs })),
    [trucks]
  );

  const currentGroup = stageGroups[stageIdx] ?? stageGroups[0];
  const prevStage    = stageIdx > 0 ? PIPELINE[stageIdx - 1] : null;
  const nextStage    = stageIdx < PIPELINE.length - 1 ? PIPELINE[stageIdx + 1] : null;
  const movingTruck  = movingTruckId ? trucks.find(t => t.id === movingTruckId) : null;
  const alreadyHere  = movingTruck
    ? currentGroup.stage.statuses.includes(movingTruck.status)
    : false;

  function goLeft()  { setStageIdx(i => Math.max(0, i - 1)); }
  function goRight() { setStageIdx(i => Math.min(PIPELINE.length - 1, i + 1)); }

  function onBoardTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }

  function onBoardTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (Math.abs(dx) > 55 && Math.abs(dx) > dy * 1.4) {
      if (dx < 0) goRight();
      else goLeft();
    }
    touchStartX.current = null;
  }

  function handleLongPress(truck) {
    setMovingTruckId(truck.id);
  }

  function cancelMove() {
    setMovingTruckId(null);
  }

  async function confirmMove() {
    if (!movingTruckId || saving || alreadyHere) return;
    setSaving(true);
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: {
          input: {
            id: movingTruckId,
            status: currentGroup.stage.statuses[0],
            lastStatusChange: Math.floor(Date.now() / 1000),
          },
        },
      });
      await fetchAll({ quiet: true });
      setMovingTruckId(null);
    } catch (err) {
      console.error('Failed to move truck:', err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 24, color: '#64748b', textAlign: 'center', paddingTop: 60 }}>
        Loading shop...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 18 }}>
        <div style={{ color: '#dc2626', fontWeight: 800, marginBottom: 10 }}>Unable to load shop</div>
        <button
          type="button"
          onClick={() => fetchAll()}
          style={{ border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', padding: '10px 18px' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{ minHeight: 'calc(100vh - 52px)', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}
      onTouchStart={onBoardTouchStart}
      onTouchEnd={onBoardTouchEnd}
    >
      {/* Move mode banner */}
      {movingTruck && (
        <div style={{
          background: '#fef3c7',
          borderBottom: '2px solid #f59e0b',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
            Moving: {movingTruck.repairOrder} — {movingTruck.companyName || 'No company'}
          </span>
          <button
            onClick={cancelMove}
            style={{ background: 'none', border: 'none', color: '#dc2626', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: '4px 8px' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Stage navigation header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: '#0f172a',
        flexShrink: 0,
        minHeight: 64,
      }}>
        <button
          type="button"
          onClick={goLeft}
          disabled={stageIdx === 0}
          style={{
            background: 'none', border: 'none',
            color: stageIdx === 0 ? '#1e293b' : '#64748b',
            cursor: stageIdx === 0 ? 'default' : 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '8px 10px', gap: 3, minWidth: 70, flexShrink: 0,
          }}
        >
          <ChevronLeft size={22} />
          {prevStage && (
            <span style={{
              fontSize: 9, fontWeight: 600, letterSpacing: 0.3,
              maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', textAlign: 'center',
            }}>
              {prevStage.label}
            </span>
          )}
        </button>

        <div style={{ flex: 1, textAlign: 'center', padding: '0 4px' }}>
          <div style={{ color: '#f1f5f9', fontWeight: 900, fontSize: 18, letterSpacing: 0.2 }}>
            {currentGroup.stage.label}
          </div>
          <div style={{ color: '#475569', fontSize: 11, fontWeight: 700, marginTop: 2 }}>
            {currentGroup.trucks.length} unit{currentGroup.trucks.length !== 1 ? 's' : ''}
            &nbsp;·&nbsp;{stageIdx + 1} of {PIPELINE.length}
          </div>
        </div>

        <button
          type="button"
          onClick={goRight}
          disabled={stageIdx === PIPELINE.length - 1}
          style={{
            background: 'none', border: 'none',
            color: stageIdx === PIPELINE.length - 1 ? '#1e293b' : '#64748b',
            cursor: stageIdx === PIPELINE.length - 1 ? 'default' : 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '8px 10px', gap: 3, minWidth: 70, flexShrink: 0,
          }}
        >
          <ChevronRight size={22} />
          {nextStage && (
            <span style={{
              fontSize: 9, fontWeight: 600, letterSpacing: 0.3,
              maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', textAlign: 'center',
            }}>
              {nextStage.label}
            </span>
          )}
        </button>
      </div>

      {/* Stage dots */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 6,
        padding: '7px 16px',
        background: '#0f172a',
        borderBottom: '1px solid #1e293b',
        flexShrink: 0,
      }}>
        {stageGroups.map(({ stage, trucks: stageTrucks }, i) => {
          const isActive = i === stageIdx;
          const hasUnits = stageTrucks.length > 0;
          return (
            <button
              key={stage.id}
              type="button"
              onClick={() => setStageIdx(i)}
              title={`${stage.label} (${stageTrucks.length})`}
              style={{
                width: isActive ? 22 : 8,
                height: 8,
                borderRadius: 4,
                background: isActive ? '#f59e0b' : hasUnits ? '#475569' : '#1e293b',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'width 0.22s ease, background 0.2s',
                flexShrink: 0,
              }}
            />
          );
        })}
      </div>

      {/* Card list */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: movingTruck ? '12px 12px 140px' : '12px 12px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}>
        {!movingTruck && (
          <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginBottom: 4 }}>
            Hold a card to move it between stages
          </div>
        )}
        {currentGroup.trucks.map(truck => (
          <MobileTruckCard
            key={truck.id}
            truck={truck}
            onLongPress={handleLongPress}
            isBeingMoved={truck.id === movingTruckId}
            moveMode={!!movingTruckId}
          />
        ))}
        {!currentGroup.trucks.length && (
          <div style={{
            color: '#94a3b8',
            background: '#fff',
            border: '1px dashed #e2e8f0',
            borderRadius: 8,
            padding: 40,
            textAlign: 'center',
            fontSize: 14,
          }}>
            No units in {currentGroup.stage.label}
          </div>
        )}
      </div>

      {/* Move mode: drop panel */}
      {movingTruck && (
        <div style={{
          position: 'fixed',
          bottom: 0, left: 0, right: 0,
          background: '#0f172a',
          borderTop: '2px solid #f59e0b',
          padding: '12px 16px 32px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          zIndex: 150,
        }}>
          <div style={{ color: '#475569', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
            Swipe left / right to choose a stage, then drop
          </div>
          <button
            type="button"
            onClick={confirmMove}
            disabled={saving || alreadyHere}
            style={{
              background: alreadyHere ? '#1e293b' : '#f59e0b',
              color: alreadyHere ? '#475569' : '#0f172a',
              border: 'none',
              borderRadius: 10,
              padding: '15px',
              fontSize: 16,
              fontWeight: 800,
              cursor: (saving || alreadyHere) ? 'default' : 'pointer',
              letterSpacing: 0.2,
            }}
          >
            {saving
              ? 'Moving...'
              : alreadyHere
              ? `Already in ${currentGroup.stage.label}`
              : `Move to ${currentGroup.stage.label}`}
          </button>
        </div>
      )}
    </div>
  );
}
